import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Monitor, Info, Cpu, HardDrive, Building2, MapPin, Save, X, FileText, Wifi, Printer, BatteryCharging, Network, Layers } from 'lucide-react';
import SearchableSelect from "../ui/SearchableSelect";

const TIPOS_EQUIPO = [
  "AP", "DVR", "Firewall", "Impresora", "MiniPC", "NAS", "Notebook", "NVR", "PC",
  "Router", "Servidor", "Switch", "Tablet", "UPS"
];

function getCategoriaEquipo(tipo) {
  if (["PC", "Notebook", "MiniPC", "Servidor", "Tablet", "NAS"].includes(tipo)) return "computador";
  if (["Router", "AP", "Switch", "Firewall"].includes(tipo)) return "red";
  if (tipo === "Impresora") return "impresora";
  if (tipo === "UPS") return "ups";
  return "generico";
}

export default function EquipoForm({ equipo = null, onGuardado, onCancelar }) {
  const { user } = useAuth();

  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [departamentosDisponibles, setDepartamentosDisponibles] = useState([]);
  const [form, setForm] = useState({
    nombre: "", marca: "", modelo: "", numero_serie: "", tipo_equipo: "",
    // Cómputo (PC, Notebook, MiniPC, Tablet, Servidor)
    procesador: "", memoria_ram: "", tipo_ram: "", disco: "", tipo_disco: "", sistema_operativo: "",
    pantalla_pulgadas: "", conectividad: "",
    // Red (Router, AP, Switch, Firewall)
    puertos_lan: "", protocolo_wifi: "", frecuencia_wifi: "", velocidad_wan: "",
    tipo_switch: "", poe: "", firmware_version: "",
    // Impresora
    tipo_impresion: "", conexion_impresora: "", resolucion_dpi: "", formato_papel: "",
    // UPS
    capacidad_va: "", potencia_w: "", autonomia_min: "", tipo_bateria_ups: "",
    // Comunes
    estado: "operativo", ubicacion: "", observaciones: "", cliente_id: "", sucursal: ""
  });

  useEffect(() => {
    cargarClientes();
  }, []);

  useEffect(() => {
    if (equipo) {
      console.log("Equipo recibido en formulario:", equipo);
      
      // Extraer cliente_id de múltiples posibles formatos
      const clienteIdRaw = equipo.cliente_id || 
                           equipo.clienteId || 
                           equipo.cliente?.id || 
                           equipo.cliente?._id ||
                           equipo.cliente_lookup_ids?.[0] ||
                           "";
      
      console.log("Cliente ID extraído (raw):", clienteIdRaw);
      
      // Buscar el cliente en la lista para normalizar el ID
      let clienteEncontrado = null;
      let clienteIdNormalizado = clienteIdRaw;
      
      if (clienteIdRaw && clientes.length > 0) {
        clienteEncontrado = clientes.find(c => 
          c.id === clienteIdRaw || 
          c._id === clienteIdRaw || 
          c.primaryId === clienteIdRaw ||
          (c.lookupIds && c.lookupIds.includes(clienteIdRaw))
        );
        
        // Si encontramos el cliente, usar su primaryId para que coincida con el select
        if (clienteEncontrado) {
          clienteIdNormalizado = clienteEncontrado.primaryId || clienteEncontrado.id;
          console.log("Cliente encontrado - normalizado a primaryId:", clienteIdNormalizado);
        } else {
          console.warn("Cliente NO encontrado en la lista para ID:", clienteIdRaw);
        }
      }
      
      setForm({
        nombre: equipo.nombre || "",
        marca: equipo.marca || "",
        modelo: equipo.modelo || "",
        numero_serie: equipo.numero_serie || "",
        tipo_equipo: equipo.tipo_equipo || "",
        procesador: equipo.procesador || "",
        memoria_ram: equipo.memoria_ram || equipo.ram || "",
        tipo_ram: equipo.tipo_ram || "",
        disco: equipo.disco || "",
        tipo_disco: equipo.tipo_disco || "",
        sistema_operativo: equipo.sistema_operativo || "",
        pantalla_pulgadas: equipo.pantalla_pulgadas || "",
        conectividad: equipo.conectividad || "",
        puertos_lan: equipo.puertos_lan || "",
        protocolo_wifi: equipo.protocolo_wifi || "",
        frecuencia_wifi: equipo.frecuencia_wifi || "",
        velocidad_wan: equipo.velocidad_wan || "",
        tipo_switch: equipo.tipo_switch || "",
        poe: equipo.poe || "",
        firmware_version: equipo.firmware_version || "",
        tipo_impresion: equipo.tipo_impresion || "",
        conexion_impresora: equipo.conexion_impresora || "",
        resolucion_dpi: equipo.resolucion_dpi || "",
        formato_papel: equipo.formato_papel || "",
        capacidad_va: equipo.capacidad_va || "",
        potencia_w: equipo.potencia_w || "",
        autonomia_min: equipo.autonomia_min || "",
        tipo_bateria_ups: equipo.tipo_bateria_ups || "",
        estado: equipo.estado || "operativo",
        ubicacion: equipo.ubicacion || "",
        observaciones: equipo.comentarios || equipo.observaciones || "",
        cliente_id: clienteIdNormalizado,
        sucursal: equipo.sucursal || "",
        departamento: equipo.departamento || ""
      });
      
      setClienteSeleccionado(clienteEncontrado || null);
      // Cargar departamentos del cliente seleccionado
      if (clienteEncontrado) {
        const cId = clienteEncontrado._id || clienteEncontrado.id;
        http.get(`/clientes/${cId}/departamentos`).then(r => {
          setDepartamentosDisponibles(r.data || []);
        }).catch(() => setDepartamentosDisponibles([]));
      }
    } else {
      resetForm();
    }
  }, [equipo, clientes]);

  async function cargarClientes() {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/clientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientesData = Array.isArray(res.data) ? res.data : [];
      
      // Ordenar clientes alfabéticamente
      clientesData.sort((a, b) => (a.nombre || "").toLowerCase().localeCompare((b.nombre || "").toLowerCase()));

      // Normalizar clientes con primaryId y lookupIds
      const clientesNormalizados = clientesData.map(c => {
        const lookupIds = [
          c.id, 
          c._id, 
          c.mongo_id,
          c.primaryId,
          ...(c.lookupIds || [])
        ].filter(Boolean).map(String);
        
        return {
          ...c,
          primaryId: c.id || c._id || c.primaryId || lookupIds[0],
          lookupIds: [...new Set(lookupIds)]
        };
      });
      
      console.log("Clientes cargados:", clientesNormalizados);
      setClientes(clientesNormalizados);
    } catch (err) {
      console.error("Error cargando clientes", err);
    }
  }

  function resetForm() {
    setForm({
      nombre: "", marca: "", modelo: "", numero_serie: "", tipo_equipo: "",
      procesador: "", memoria_ram: "", tipo_ram: "", disco: "", tipo_disco: "", sistema_operativo: "",
      pantalla_pulgadas: "", conectividad: "",
      puertos_lan: "", protocolo_wifi: "", frecuencia_wifi: "", velocidad_wan: "",
      tipo_switch: "", poe: "", firmware_version: "",
      tipo_impresion: "", conexion_impresora: "", resolucion_dpi: "", formato_papel: "",
      capacidad_va: "", potencia_w: "", autonomia_min: "", tipo_bateria_ups: "",
      estado: "operativo", ubicacion: "", observaciones: "", cliente_id: "", sucursal: "", departamento: ""
    });
  }

  async function guardarEquipo(e) {
    e.preventDefault();
    
    // Validar campos requeridos
    const camposRequeridos = {
      marca: "Marca",
      modelo: "Modelo",
      tipo_equipo: "Tipo de equipo",
      cliente_id: "Cliente"
    };

    for (const [campo, nombre] of Object.entries(camposRequeridos)) {
      if (!form[campo]?.trim()) {
        alert(`El campo ${nombre} es requerido`);
        return;
      }
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No hay token de autorización");
      }

      const equipoId = equipo?._id || equipo?.id;
      
      if (equipo && !equipoId) {
        throw new Error("ID de equipo no válido");
      }

      let res;
      
      if (equipo) {
        // ACTUALIZAR: Usar JSON (PUT /equipos/{id})
        const dataToSend = {
          ...form,
          nombre: form.nombre.trim(),
          marca: form.marca.trim(),
          modelo: form.modelo.trim(),
          tipo_equipo: form.tipo_equipo.trim(),
          numero_serie: form.numero_serie.trim(),
          estado: form.estado || "operativo",
          ubicacion: form.ubicacion.trim(),
          memoria_ram: form.memoria_ram != null ? String(form.memoria_ram).trim() : null,
          tipo_ram: form.tipo_ram != null ? String(form.tipo_ram).trim() : null,
          disco: form.disco != null ? String(form.disco).trim() : null,
          cliente_id: form.cliente_id || null
        };

        console.log("Actualizando equipo:", equipoId, dataToSend);
        
        res = await http.put(
          `/equipos/${equipoId}`, 
          dataToSend, 
          { 
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // CREAR: Usar FormData (POST /equipos/)
        const formData = new FormData();
        formData.append('nombre', form.nombre.trim() || `${form.marca} ${form.modelo}`.trim());
        formData.append('descripcion', form.descripcion?.trim() || form.nombre.trim() || `${form.marca} ${form.modelo}`.trim());
        formData.append('marca', form.marca.trim());
        formData.append('modelo', form.modelo.trim());
        formData.append('tipo_equipo', form.tipo_equipo.trim());
        formData.append('numero_serie', form.numero_serie?.trim() || '');
        formData.append('estado', form.estado || 'operativo');
        formData.append('ubicacion', form.ubicacion?.trim() || '');
        formData.append('cliente_id', form.cliente_id);
        formData.append('sucursal', form.sucursal?.trim() || '');
        formData.append('departamento', form.departamento?.trim() || '');
        formData.append('sistema_operativo', form.sistema_operativo?.trim() || '');
        formData.append('procesador', form.procesador?.trim() || '');
        formData.append('memoria_ram', form.memoria_ram != null ? String(form.memoria_ram).trim() : '');
        formData.append('tipo_ram', form.tipo_ram != null ? String(form.tipo_ram).trim() : '');
        formData.append('disco', form.disco != null ? String(form.disco).trim() : '');
        formData.append('tipo_disco', form.tipo_disco != null ? String(form.tipo_disco).trim() : '');
        formData.append('pantalla_pulgadas', form.pantalla_pulgadas?.trim() || '');
        formData.append('conectividad', form.conectividad?.trim() || '');
        formData.append('puertos_lan', form.puertos_lan?.trim() || '');
        formData.append('protocolo_wifi', form.protocolo_wifi?.trim() || '');
        formData.append('frecuencia_wifi', form.frecuencia_wifi?.trim() || '');
        formData.append('velocidad_wan', form.velocidad_wan?.trim() || '');
        formData.append('tipo_switch', form.tipo_switch?.trim() || '');
        formData.append('poe', form.poe?.trim() || '');
        formData.append('firmware_version', form.firmware_version?.trim() || '');
        formData.append('tipo_impresion', form.tipo_impresion?.trim() || '');
        formData.append('conexion_impresora', form.conexion_impresora?.trim() || '');
        formData.append('resolucion_dpi', form.resolucion_dpi?.trim() || '');
        formData.append('formato_papel', form.formato_papel?.trim() || '');
        formData.append('capacidad_va', form.capacidad_va?.trim() || '');
        formData.append('potencia_w', form.potencia_w?.trim() || '');
        formData.append('autonomia_min', form.autonomia_min?.trim() || '');
        formData.append('tipo_bateria_ups', form.tipo_bateria_ups?.trim() || '');
        formData.append('observaciones', form.observaciones?.trim() || '');

        console.log("Creando equipo nuevo con FormData");
        
        // Para FormData, necesitamos configuración especial
        res = await http.post(
          "/equipos/", 
          formData, 
          { 
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
              // axios automáticamente añadirá el boundary
            }
          }
        );
      }

      if (!res.data) {
        throw new Error("No se recibió respuesta del servidor");
      }

      console.log("Respuesta del servidor:", res.data);
      alert("✅ Equipo guardado correctamente");
      onGuardado();
    } catch (err) {
      console.error("Error guardando equipo", err);
      console.log("Detalles de la solicitud:", {
        url: err.config?.url,
        method: err.config?.method,
        headers: err.config?.headers,
        data: err.config?.data
      });
      console.log("Detalles de la respuesta:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      let mensaje = "Error al guardar equipo";
      
      if (err.response) {
        // Error de respuesta del servidor
        if (err.response.status === 405) {
          mensaje = "Método no permitido. Por favor, actualiza la página y vuelve a intentarlo.";
          // Intentar determinar qué métodos están permitidos
          const allowedMethods = err.response.headers?.['allow'];
          if (allowedMethods) {
            console.log("Métodos permitidos:", allowedMethods);
          }
        } else {
          mensaje = err.response.data?.detail || "Error en la respuesta del servidor";
        }
      } else if (err.request) {
        // Error de red
        mensaje = "No se pudo conectar con el servidor. Verifica tu conexión.";
      } else {
        // Error de código
        mensaje = err.message;
      }
      
      alert(`❌ ${mensaje}`);
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-purple-600 px-5 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 backdrop-blur-sm rounded-lg">
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">
                {equipo ? "Editar Equipo" : "Nuevo Equipo"}
              </h3>
              <p className="text-xs text-white/80">
                {equipo ? "Actualiza la información" : "Registra un nuevo equipo"}
              </p>
            </div>
          </div>
          <button
            onClick={onCancelar}
            className="p-1.5 text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          <form id="equipoForm" onSubmit={guardarEquipo} className="space-y-4">
            
        {/* Información General */}
        <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-xl p-4 border border-emerald-100">
          <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Info className="w-4 h-4 text-emerald-600" />
            Información General
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="nombre" className="block text-xs font-medium text-gray-700 mb-1">
                Nombre del Equipo *
              </label>
              <input 
              id="nombre" 
              name="nombre" 
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Ej: PC-001" 
              value={form.nombre} 
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} 
              required 
            />
            </div>
            <div>
              <label htmlFor="marca" className="block text-xs font-medium text-gray-700 mb-1">
                Marca *
              </label>
              <input 
              id="marca" 
              name="marca" 
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Ej: HP, Dell, Lenovo" 
              value={form.marca} 
              onChange={(e) => setForm({ ...form, marca: e.target.value })} 
              required 
            />
            </div>
            <div>
              <label htmlFor="modelo" className="block text-xs font-medium text-gray-700 mb-1">
                Modelo *
              </label>
              <input 
              id="modelo" 
              name="modelo" 
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" 
              placeholder="Ej: Latitude 5400" 
              value={form.modelo} 
              onChange={(e) => setForm({ ...form, modelo: e.target.value })} 
              required 
            />
            </div>
            <div>
              <label htmlFor="numero_serie" className="block text-xs font-medium text-gray-700 mb-1">
                Número de Serie
              </label>
              <input 
              id="numero_serie" 
              name="numero_serie" 
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono" 
              placeholder="Ej: ABC123XYZ" 
              value={form.numero_serie} 
              onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} 
            />
            </div>
            <div>
              <label htmlFor="tipo_equipo" className="block text-xs font-medium text-gray-700 mb-1">
                Tipo de Equipo *
              </label>
              <select 
                id="tipo_equipo" 
                name="tipo_equipo" 
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" 
                value={form.tipo_equipo} 
                onChange={(e) => setForm({ ...form, tipo_equipo: e.target.value })} 
                required
              >
                <option value="">Seleccionar tipo de equipo</option>
                {TIPOS_EQUIPO.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Especificaciones Técnicas — Dinámicas según tipo */}
        {form.tipo_equipo ? (
          <>
            {/* CÓMPUTO: PC, Notebook, MiniPC, Servidor, Tablet */}
            {getCategoriaEquipo(form.tipo_equipo) === 'computador' && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-purple-600" />
                  Especificaciones de Cómputo
                  {form.tipo_equipo === 'Tablet' && <span className="ml-auto text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">Tablet</span>}
                  {form.tipo_equipo === 'Notebook' && <span className="ml-auto text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">Notebook</span>}
                  {form.tipo_equipo === 'Servidor' && <span className="ml-auto text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">Servidor</span>}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {form.tipo_equipo === 'Tablet' ? 'Procesador / SoC' : 'Procesador'}
                    </label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder={form.tipo_equipo === 'Tablet' ? 'Ej: Apple A16, Snapdragon 8 Gen 2' : 'Ej: Intel Core i5-1135G7'}
                      value={form.procesador || ""} onChange={(e) => setForm({ ...form, procesador: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Memoria RAM</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Ej: 8GB, 16GB" value={form.memoria_ram || ""} onChange={(e) => setForm({ ...form, memoria_ram: e.target.value })} />
                  </div>
                  {form.tipo_equipo !== 'Tablet' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de RAM</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        value={form.tipo_ram || ""} onChange={(e) => setForm({ ...form, tipo_ram: e.target.value })}>
                        <option value="">Seleccionar tipo de RAM</option>
                        <option value="DDR3">DDR3</option>
                        <option value="DDR4">DDR4</option>
                        <option value="DDR5">DDR5</option>
                        <option value="SODIMM DDR3">SODIMM DDR3</option>
                        <option value="SODIMM DDR4">SODIMM DDR4</option>
                        <option value="SODIMM DDR5">SODIMM DDR5</option>
                        <option value="LPDDR4">LPDDR4 (Portátil)</option>
                        <option value="LPDDR5">LPDDR5 (Portátil)</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {form.tipo_equipo === 'Tablet' ? 'Almacenamiento Interno' : 'Almacenamiento'}
                    </label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Ej: 256GB, 1TB" value={form.disco || ""} onChange={(e) => setForm({ ...form, disco: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Almacenamiento</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder={form.tipo_equipo === 'Tablet' ? 'Ej: eMMC, UFS 3.1, NVMe' : 'Ej: SSD, HDD, NVMe'}
                      value={form.tipo_disco || ""} onChange={(e) => setForm({ ...form, tipo_disco: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Sistema Operativo</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder={form.tipo_equipo === 'Tablet' ? 'Ej: Android 14, iPadOS 17' : 'Ej: Windows 11, Ubuntu 22.04'}
                      value={form.sistema_operativo || ""} onChange={(e) => setForm({ ...form, sistema_operativo: e.target.value })} />
                  </div>
                  {['Notebook', 'Tablet'].includes(form.tipo_equipo) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Pantalla (pulgadas)</label>
                      <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Ej: 10.2, 13.3, 15.6" value={form.pantalla_pulgadas || ""} onChange={(e) => setForm({ ...form, pantalla_pulgadas: e.target.value })} />
                    </div>
                  )}
                  {form.tipo_equipo === 'Tablet' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Conectividad</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        value={form.conectividad || ""} onChange={(e) => setForm({ ...form, conectividad: e.target.value })}>
                        <option value="">Seleccionar</option>
                        <option value="WiFi">WiFi</option>
                        <option value="WiFi + 4G">WiFi + 4G (LTE)</option>
                        <option value="WiFi + 5G">WiFi + 5G</option>
                        <option value="Solo Cellular">Solo Cellular</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Estado del Equipo</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                      <option value="operativo">Operativo</option>
                      <option value="en_reparacion">En reparación</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="fuera_servicio">Fuera de servicio</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* RED: Router, AP, Switch, Firewall */}
            {getCategoriaEquipo(form.tipo_equipo) === 'red' && (
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-50 rounded-xl p-4 border border-emerald-100">
                <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Network className="w-4 h-4 text-emerald-600" />
                  Especificaciones de Red
                  <span className="ml-auto text-xs font-normal text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{form.tipo_equipo}</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Puertos LAN</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Ej: 4x GbE, 8x 10/100/1000" value={form.puertos_lan || ""} onChange={(e) => setForm({ ...form, puertos_lan: e.target.value })} />
                  </div>
                  {['Router', 'AP'].includes(form.tipo_equipo) && (<>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Protocolo WiFi</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={form.protocolo_wifi || ""} onChange={(e) => setForm({ ...form, protocolo_wifi: e.target.value })}>
                        <option value="">Sin WiFi / No aplica</option>
                        <option value="802.11n (Wi-Fi 4)">802.11n — Wi-Fi 4</option>
                        <option value="802.11ac (Wi-Fi 5)">802.11ac — Wi-Fi 5</option>
                        <option value="802.11ax (Wi-Fi 6)">802.11ax — Wi-Fi 6</option>
                        <option value="802.11ax (Wi-Fi 6E)">802.11ax — Wi-Fi 6E</option>
                        <option value="802.11be (Wi-Fi 7)">802.11be — Wi-Fi 7</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Banda / Frecuencia</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={form.frecuencia_wifi || ""} onChange={(e) => setForm({ ...form, frecuencia_wifi: e.target.value })}>
                        <option value="">Seleccionar</option>
                        <option value="2.4 GHz">2.4 GHz</option>
                        <option value="5 GHz">5 GHz</option>
                        <option value="Dual Band (2.4 + 5 GHz)">Dual Band (2.4 + 5 GHz)</option>
                        <option value="Tri Band">Tri Band</option>
                      </select>
                    </div>
                  </>)}
                  {['Router', 'Firewall'].includes(form.tipo_equipo) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Velocidad WAN</label>
                      <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Ej: 100 Mbps, 1 Gbps" value={form.velocidad_wan || ""} onChange={(e) => setForm({ ...form, velocidad_wan: e.target.value })} />
                    </div>
                  )}
                  {form.tipo_equipo === 'Switch' && (<>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Switch</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={form.tipo_switch || ""} onChange={(e) => setForm({ ...form, tipo_switch: e.target.value })}>
                        <option value="">Seleccionar</option>
                        <option value="Gestionable">Gestionable (Managed)</option>
                        <option value="No gestionable">No gestionable (Unmanaged)</option>
                        <option value="Smart">Smart (Web Managed)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">PoE</label>
                      <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={form.poe || ""} onChange={(e) => setForm({ ...form, poe: e.target.value })}>
                        <option value="">No especificado</option>
                        <option value="Sí">Sí — PoE</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </>)}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Versión Firmware</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Ej: v2.1.4" value={form.firmware_version || ""} onChange={(e) => setForm({ ...form, firmware_version: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Estado del Equipo</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                      <option value="operativo">Operativo</option>
                      <option value="en_reparacion">En reparación</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="fuera_servicio">Fuera de servicio</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* IMPRESORA */}
            {getCategoriaEquipo(form.tipo_equipo) === 'impresora' && (
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Printer className="w-4 h-4 text-teal-600" />
                  Especificaciones de Impresora
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Impresión</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      value={form.tipo_impresion || ""} onChange={(e) => setForm({ ...form, tipo_impresion: e.target.value })}>
                      <option value="">Seleccionar</option>
                      <option value="Láser">Láser</option>
                      <option value="Inkjet">Inkjet (Inyección de tinta)</option>
                      <option value="Térmica">Térmica</option>
                      <option value="Matricial">Matricial / Impacto</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Conexión</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      value={form.conexion_impresora || ""} onChange={(e) => setForm({ ...form, conexion_impresora: e.target.value })}>
                      <option value="">Seleccionar</option>
                      <option value="USB">USB</option>
                      <option value="Red (Ethernet)">Red (Ethernet)</option>
                      <option value="WiFi">WiFi</option>
                      <option value="USB + WiFi">USB + WiFi</option>
                      <option value="USB + Red">USB + Red (Ethernet)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Resolución (DPI)</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Ej: 600x600, 1200x1200" value={form.resolucion_dpi || ""} onChange={(e) => setForm({ ...form, resolucion_dpi: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Formato de Papel</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      value={form.formato_papel || ""} onChange={(e) => setForm({ ...form, formato_papel: e.target.value })}>
                      <option value="">Seleccionar</option>
                      <option value="A4">A4</option>
                      <option value="A3">A3</option>
                      <option value="A4/A3">A4 y A3</option>
                      <option value="Carta">Carta (Letter)</option>
                      <option value="Rollo 80mm">Rollo 80mm (Térmica)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Estado del Equipo</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                      <option value="operativo">Operativo</option>
                      <option value="en_reparacion">En reparación</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="fuera_servicio">Fuera de servicio</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* UPS */}
            {getCategoriaEquipo(form.tipo_equipo) === 'ups' && (
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-100">
                <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <BatteryCharging className="w-4 h-4 text-yellow-600" />
                  Especificaciones UPS
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Capacidad (VA)</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="Ej: 600, 1000, 1500" value={form.capacidad_va || ""} onChange={(e) => setForm({ ...form, capacidad_va: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Potencia (W)</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="Ej: 360, 600, 900" value={form.potencia_w || ""} onChange={(e) => setForm({ ...form, potencia_w: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Autonomía (minutos)</label>
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="Ej: 15, 30, 60" value={form.autonomia_min || ""} onChange={(e) => setForm({ ...form, autonomia_min: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Batería</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      value={form.tipo_bateria_ups || ""} onChange={(e) => setForm({ ...form, tipo_bateria_ups: e.target.value })}>
                      <option value="">Seleccionar</option>
                      <option value="VRLA / SLA">VRLA / SLA (Sellada)</option>
                      <option value="Litio-Ion">Litio-Ion (Li-Ion)</option>
                      <option value="NiMH">NiMH</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Estado del Equipo</label>
                    <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                      <option value="operativo">Operativo</option>
                      <option value="en_reparacion">En reparación</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="fuera_servicio">Fuera de servicio</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* GENÉRICO */}
            {getCategoriaEquipo(form.tipo_equipo) === 'generico' && (
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-200">
                <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-gray-600" />
                  Estado del Equipo
                </h4>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                  <select className="w-full md:w-1/2 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                    value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                    <option value="operativo">Operativo</option>
                    <option value="en_reparacion">En reparación</option>
                    <option value="mantenimiento">Mantenimiento</option>
                    <option value="fuera_servicio">Fuera de servicio</option>
                  </select>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
            <Monitor className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 font-medium">Seleccione un tipo de equipo para ver las especificaciones técnicas</p>
            <p className="text-xs text-gray-400 mt-1">Los campos se adaptarán automáticamente al tipo seleccionado</p>
          </div>
        )}

        {/* Cliente y Ubicación */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
          <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-green-600" />
            Cliente y Ubicación
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="cliente_id" className="block text-xs font-medium text-gray-700 mb-1">
                Cliente *
              </label>
              <SearchableSelect
                placeholder="Seleccionar cliente"
                className="w-full"
                value={form.cliente_id}
                options={clientes.map(c => ({ value: c.primaryId || c.id, label: c.nombre }))}
                onChange={(selectedId) => {
                  const cliente = clientes.find(c => 
                    c.primaryId === selectedId || 
                    c.id === selectedId || 
                    c._id === selectedId ||
                    (c.lookupIds && c.lookupIds.includes(selectedId))
                  );
                  setClienteSeleccionado(cliente || null);
                  setForm({ ...form, cliente_id: selectedId, sucursal: "", departamento: "" });
                  // Cargar departamentos del cliente
                  if (cliente) {
                    const cId = cliente._id || cliente.id;
                    http.get(`/clientes/${cId}/departamentos`).then(r => {
                      setDepartamentosDisponibles(r.data || []);
                    }).catch(() => setDepartamentosDisponibles([]));
                  } else {
                    setDepartamentosDisponibles([]);
                  }
                }}
                required
              />
            </div>
            
            {clienteSeleccionado?.sucursales && clienteSeleccionado.sucursales.length > 0 ? (
              <div>
                <label htmlFor="sucursal" className="block text-xs font-medium text-gray-700 mb-1">
                  Sucursal
                </label>
                <select 
                  id="sucursal" 
                  name="sucursal" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                  value={form.sucursal} 
                  onChange={(e) => setForm({ ...form, sucursal: e.target.value })}
                >
                  <option value="">Seleccionar sucursal (opcional)</option>
                  {clienteSeleccionado.sucursales.map((suc, idx) => {
                    const nombre = typeof suc === 'string' ? suc : suc.nombre;
                    return (
                      <option key={idx} value={nombre}>
                        {nombre}
                      </option>
                    );
                  })}
                </select>
              </div>
            ) : (
              <div>
                <label htmlFor="sucursal" className="block text-xs font-medium text-gray-700 mb-1">
                  Sucursal
                </label>
                <input 
                  id="sucursal" 
                  name="sucursal" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                  placeholder="Sucursal (opcional)" 
                  value={form.sucursal} 
                  onChange={(e) => setForm({ ...form, sucursal: e.target.value })} 
                />
              </div>
            )}
            
            {/* Departamento */}
            {departamentosDisponibles.length > 0 && (
              <div>
                <label htmlFor="departamento" className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  {clienteSeleccionado?.configuracion_ubicacion?.label_departamento || 'Departamento'}
                </label>
                <select 
                  id="departamento" 
                  name="departamento" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                  value={form.departamento} 
                  onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                >
                  <option value="">Seleccionar (opcional)</option>
                  {departamentosDisponibles
                    .filter(d => !form.sucursal || !d.sucursal_nombre || d.sucursal_nombre === form.sucursal)
                    .map((dep, idx) => (
                      <option key={idx} value={dep.nombre}>{dep.nombre}</option>
                    ))}
                </select>
              </div>
            )}

            <div className="md:col-span-2">
              <label htmlFor="ubicacion" className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Ubicación Física
              </label>
              <input 
                id="ubicacion" 
                name="ubicacion" 
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" 
                placeholder="Ej: Oficina principal, 2do piso" 
                value={form.ubicacion} 
                onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} 
              />
            </div>
          </div>
        </div>

        {/* Observaciones */}
        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-600" />
            Observaciones
          </h4>
          <textarea 
            id="observaciones" 
            name="observaciones" 
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none" 
            rows={3} 
            placeholder="Notas adicionales sobre el equipo..." 
            value={form.observaciones} 
            onChange={(e) => setForm({ ...form, observaciones: e.target.value })} 
          />
        </div>

          </form>
        </div>

        {/* Footer - Botones */}
        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button 
            type="button" 
            className="px-5 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center gap-2" 
            onClick={onCancelar}
          >
            <X className="w-4 h-4" />
            Cancelar
          </button>
          <button 
            type="submit" 
            form="equipoForm"
            className="px-5 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors font-medium flex items-center gap-2 shadow-md" 
            onClick={guardarEquipo}
          >
            <Save className="w-4 h-4" />
            Guardar
          </button>
        </div>
      </div>
    </div>
  , document.body);
}